import { useRouteLoaderData } from "@remix-run/react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Loader2, Minus, Plus } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ServiceTypeSelect from "~/components/booking/flow/ServiceTypeSelect";
import { Button } from "~/components/ui/Button";
import { keepAnimationOnMainThread } from "~/lib/motion";
import { cn, getCmsServiceFromList } from "~/lib/utils";
import { useBookingCart as useBookingCartStore } from "~/stores/bookingCartContext";

// ACF `true_false` on the `service_category` term. Strict on purpose: a
// missing field (category not migrated yet) must keep the old default.
const isPreselectGuestsCountEnabled = (category: any) => {
  const flag = category?.acf?.preselect_guests_count;
  return flag === true || flag === 1 || flag === "1";
};

interface UpsellingStepProps {
  selectedUpsellingService: any;
  selectedUpsellingTime: any[];
  onSelectService: (cmsService: any, service?: any) => void;
  onSelectTime: (time: any) => void;
  onSetSelectedTimes?: (times: any[]) => void;
  onBack: () => void;
  onCancel: () => void;
  onContinue: (quantity: number) => void;
  onSkip: () => void;
  externalLoading: boolean;
  selectedDate: Date;
  isVoucherBooking?: boolean;
  guestsCount: { male: number; female: number };
  bookingCart?: any;
  onLoadingChange?: (loading: boolean) => void;
  onOpenCart?: () => void;
}

const UpsellingStep = ({
  selectedUpsellingService,
  selectedUpsellingTime,
  onSelectService,
  onSelectTime,
  onSetSelectedTimes,
  onBack,
  onCancel,
  onContinue,
  onSkip,
  externalLoading,
  selectedDate,
  guestsCount,
  bookingCart,
  isVoucherBooking = false,
  onLoadingChange,
  onOpenCart,
}: UpsellingStepProps) => {
  const { t, i18n } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);
  const rootData = useRouteLoaderData<any>("root");
  const { bookingCart: bookingCartFromStore } = useBookingCartStore();
  const [upsellingServices, setUpsellingServices] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [isLoadingTime, setIsLoadingTime] = useState(false);
  const [isRefreshingUpselling, setIsRefreshingUpselling] = useState(false);
  const totalGuests = guestsCount.male + guestsCount.female;
  const [selectedQuantity, setSelectedQuantity] = useState(totalGuests);

  const calculateInitialQuantity = (service: any) => {
    // `preselect_guests_count` on the service's upselling category: the
    // "Per quante persone?" selector starts from the whole party, within the
    // selector's own maximum. Absent or off keeps the default below.
    if (isPreselectGuestsCountEnabled(service?.category)) {
      const numPax = service?.NumPax > 1 ? service.NumPax : 1;
      return Math.max(1, Math.floor(totalGuests / numPax));
    }
    if (!service?.NumPax || service.NumPax <= 1) {
      return 1;
    }
    return Math.ceil(totalGuests / service.NumPax);
  };

  const lastFetchKeyRef = useRef<string | null>(null);
  const justDeselectedTimeRef = useRef<string | null>(null);
  const isRefreshingRef = useRef<boolean>(false);
  const previousSelectedTimesRef = useRef<any[]>([]);
  const previousSelectedServiceRef = useRef<any>(null);

  const currentBookingCart = bookingCart || bookingCartFromStore;

  const upsellingCategories = rootData.upsellingServiceCategories || [];

  // Codici dei servizi già presenti nel carrello come parte del pacchetto
  // (riga MAIN del pacchetto + righe INCLUDED). Questi NON devono comparire
  // tra gli upselling, nemmeno quando il loro code è `show_anyway`: se un
  // servizio è già incluso nel pacchetto prenotato non ha senso riproporlo
  // come add-on. Escludiamo SOLO MAIN/INCLUDED: gli upselling già aggiunti
  // (Role INTEGRATION) devono restare visibili e deselezionabili.
  const packageServiceCodes = useMemo(() => {
    const codes = new Set<string>();
    (currentBookingCart?.BasketItems || []).forEach((item: any) => {
      if (item?.Service && item.Role !== "INTEGRATION") {
        codes.add(item.Service);
      }
    });
    return codes;
  }, [currentBookingCart]);

  const servicesByCategory = useMemo(() => {
    const groups: Array<{ category: any; services: any[] }> = [];
    upsellingCategories.forEach((category: any) => {
      const services = upsellingServices.filter(
        (service) =>
          service.category?.acf?.code === category.acf?.code &&
          !packageServiceCodes.has(service.Code)
      );
      if (services.length > 0) {
        groups.push({ category, services });
      }
    });
    return groups;
  }, [upsellingCategories, upsellingServices, packageServiceCodes]);

  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);

  const totalCategories = servicesByCategory.length;
  const currentCategoryGroup =
    totalCategories > 0
      ? servicesByCategory[Math.min(currentCategoryIndex, totalCategories - 1)]
      : null;
  const isLastCategory =
    totalCategories > 0 && currentCategoryIndex >= totalCategories - 1;

  const advanceCategory = useCallback(() => {
    if (currentCategoryIndex >= totalCategories - 1) {
      onSkip();
    } else {
      setCurrentCategoryIndex((i) => i + 1);
    }
  }, [currentCategoryIndex, totalCategories, onSkip]);

  const currentSelectedService = useMemo(() => {
    if (!selectedUpsellingService) return null;
    const updatedService = upsellingServices.find(
      (service) => service.Code === selectedUpsellingService.Code
    );
    return updatedService || selectedUpsellingService;
  }, [selectedUpsellingService, upsellingServices]);

  const normalizeTime = (time: string): string => {
    if (!time) return "";
    if (time.includes("T")) {
      const timePart = time.split("T")[1];
      return timePart ? timePart.split(".")[0] : time;
    }
    return time.split(".")[0];
  };

  const timesMatch = (time1: string, time2: string): boolean => {
    return normalizeTime(time1) === normalizeTime(time2);
  };

  const handleTimeSelection = async (time: any) => {
    if (isLoadingTime || !selectedUpsellingService) return;

    const timeId = `${normalizeTime(time.StartTime)}-${normalizeTime(
      time.EndTime
    )}`;
    const isCurrentlySelected = selectedUpsellingTime.some(
      (t: any) =>
        timesMatch(t.StartTime, time.StartTime) &&
        timesMatch(t.EndTime, time.EndTime)
    );

    // If already selected and trying to deselect, handle it separately
    if (isCurrentlySelected) {
      // Allow deselection only if not loading
      if (!isLoadingTime) {
        const deselectedTimeKey = `${normalizeTime(
          time.StartTime
        )}-${normalizeTime(time.EndTime)}`;
        justDeselectedTimeRef.current = deselectedTimeKey;
        onSelectTime(time);
        setIsLoadingTime(true);
      }
      return;
    }

    setIsLoadingTime(true);

    // Call onSelectTime to add the time to cart (allows multiple selections)
    onSelectTime(time);
  };

  const refreshAvailability = useCallback(
    async (isTimeSelection: boolean = false) => {
      try {
        if (!isTimeSelection) {
          setIsRefreshingUpselling(true);
        }
        const cart = currentBookingCart;

        const mainService = cart?.BasketItems?.find(
          (item: any) => item.Role === "MAIN"
        );
        const mainServiceStartTime = mainService?.StartTime || "";
        const mainServiceEndTime = mainService?.EndTime || "";

        const currentSelectedTimes = selectedUpsellingTime;

        const basketItems: any[] = [];
        if (selectedUpsellingService && currentSelectedTimes.length > 0) {
          const cartItemsForService =
            cart?.BasketItems?.filter(
              (item: any) =>
                item.Role === "INTEGRATION" &&
                item.Service === selectedUpsellingService.Code
            ) || [];
          const existingTimeKeys = new Set(
            cartItemsForService.map(
              (item: any) =>
                `${normalizeTime(item.StartTime)}-${normalizeTime(
                  item.EndTime
                )}`
            )
          );

          currentSelectedTimes.forEach((time: any, timeIndex: number) => {
            const timeKey = `${normalizeTime(time.StartTime)}-${normalizeTime(
              time.EndTime
            )}`;
            if (!existingTimeKeys.has(timeKey)) {
              const price = time.EffectivePrice?.[0] ||
                time.Price || { Price: 0, Currency: "EUR", VatIncluded: true };
              const numPax = selectedUpsellingService?.NumPax || 1;
              const totalPersons = selectedQuantity * numPax;

              for (let i = 0; i < totalPersons; i++) {
                const basketItem: any = {
                  Type: "SERVICE",
                  ServiceType: selectedUpsellingService.ProductType,
                  LineNr: basketItems.length + 1,
                  Service: selectedUpsellingService.Code,
                  Role: "INTEGRATION",
                  CheckinDate: selectedDate.toISOString().split("T")[0],
                  StartTime: time.StartTime,
                  EndTime: time.EndTime,
                  Quantity: {
                    Total: 1,
                    Man: 0,
                    Woman: 0,
                    ThirdType: 0,
                  },
                  GrossPrice: price,
                };
                basketItems.push(basketItem);
              }
            }
          });
        }

        const vouchers = isVoucherBooking
          ? currentBookingCart?.vouchers || []
          : [];
        const vouchersArray = isVoucherBooking
          ? vouchers.map((v: any) => ({
            VoucherCode: v.code,
            GiftCardType: v.giftCardType,
          }))
          : [];

        const formattedDate = selectedDate.toISOString().split("T")[0];
        const params: Record<string, string> = {
          checkinDate: formattedDate,
          startTime: mainServiceStartTime,
          endTime: mainServiceEndTime,
          basketItems: encodeURIComponent(JSON.stringify(basketItems)),
          men: guestsCount.male.toString(),
          women: guestsCount.female.toString(),
        };

        if (isVoucherBooking && vouchersArray.length > 0) {
          params.vouchers = encodeURIComponent(JSON.stringify(vouchersArray));
        }

        const url = `/api/booking/availability/upselling?${new URLSearchParams(
          params
        ).toString()}`;

        const response = await fetch(url).then((res) => res.json());
        const allServices: any[] = [];
        if (response && Array.isArray(response)) {
          response.forEach((service: any) => {
            let serviceToSet = service;
            const cmsService = getCmsServiceFromList(
              rootData.servicesData,
              serviceToSet.Code
            );

            if (cmsService) {
              // Find the category for this service
              // Category is an array, so we need to find the first matching upselling category
              let matchedCategory = null;
              const categories =
                serviceToSet.Category || serviceToSet.category || [];
              const categoryArray = Array.isArray(categories)
                ? categories
                : [categories];

              if (categoryArray.length > 0) {
                // Find the first category that matches an upselling category
                // Category can be like "CAT_TREATMENTS.CAT_SINGLE_MASSAGE" and we need to match with "CAT_TREATMENTS"
                for (const categoryItem of categoryArray) {
                  const categoryCode =
                    typeof categoryItem === "string"
                      ? categoryItem
                      : categoryItem?.code || categoryItem?.Code;

                  if (categoryCode) {
                    matchedCategory = upsellingCategories.find((cat: any) => {
                      const catCode = cat.acf?.code;
                      // Check if categoryCode includes the category code (e.g., "CAT_TREATMENTS.CAT_SINGLE_MASSAGE" includes "CAT_TREATMENTS")
                      return catCode && categoryCode.includes(catCode);
                    });

                    if (matchedCategory) {
                      break;
                    }
                  }
                }
              }

              // If not found in API response, skip this service
              if (!matchedCategory) {
                return;
              }

              allServices.push({
                ...serviceToSet,
                cmsService,
                category: matchedCategory,
              });
            }
          });
        }
        if (selectedUpsellingService && currentSelectedTimes.length > 0) {
          const updatedService = allServices.find(
            (s) => s.Code === selectedUpsellingService.Code
          );

          if (updatedService) {
            const existingTimeKeys = new Set(
              (updatedService.Availability || []).map(
                (t: any) =>
                  `${normalizeTime(t.StartTime)}-${normalizeTime(t.EndTime)}`
              )
            );

            currentSelectedTimes.forEach((selectedTime: any) => {
              const timeKey = `${normalizeTime(
                selectedTime.StartTime
              )}-${normalizeTime(selectedTime.EndTime)}`;
              if (!existingTimeKeys.has(timeKey)) {
                const timeToAdd = {
                  ...selectedTime,
                  Quantity: {
                    Total: 0,
                    Man: 0,
                    Woman: 0,
                    ThirdType: 0,
                  },
                };
                if (!updatedService.Availability) {
                  updatedService.Availability = [];
                }
                updatedService.Availability.push(timeToAdd);
              }
            });
          }
        }

        // Ghost upselling: includiamo i cmsService la cui taxonomy WP
        // (`service_category`) contiene almeno una upselling category.
        // La category puntuale si eredita dal sibling se presente nella
        // response, altrimenti si prende la prima upselling category
        // matching della taxonomy. Funziona anche se TUTTI i codes del
        // cmsService sono show_anyway (nessun sibling nella response).
        const upsellingCatById = new Map<number, any>(
          (upsellingCategories || []).map((c: any) => [c.id, c]),
        );
        (rootData.servicesData || []).forEach((cmsService: any) => {
          const taxIds: number[] = Array.isArray(cmsService?.service_category)
            ? cmsService.service_category
            : [];
          const upsellingTaxId = taxIds.find((id) =>
            upsellingCatById.has(id),
          );
          if (upsellingTaxId === undefined) return;

          const codes = cmsService.acf?.codes || [];
          const showAnywayCodes = codes.filter((c: any) => {
            const f = c?.show_anyway;
            return f === true || f === 1 || f === "1" || f === "true";
          });
          // Nel flusso VOUCHER le card "show_anyway" (info-only) non vanno
          // mostrate: si selezionano solo i servizi coperti dal voucher.
          if (isVoucherBooking || showAnywayCodes.length === 0) return;

          const sibling = allServices.find(
            (s) => s.cmsService?.id === cmsService.id,
          );
          const matchedCategory =
            sibling?.category || upsellingCatById.get(upsellingTaxId);
          if (!matchedCategory) return;

          showAnywayCodes.forEach((codeObj: any) => {
            const code = codeObj.code;
            if (!code) return;
            if (allServices.some((s) => s.Code === code)) return;

            allServices.push({
              Code: code,
              Type: "ShowAnyway",
              Name: codeObj.short_description || cmsService.acf?.title || code,
              isShowAnyway: true,
              showAnywayText: codeObj.show_anyway_text || "",
              Availability: [],
              cmsService,
              category: matchedCategory,
            });
          });
        });

        setUpsellingServices(allServices);
        if (!isTimeSelection) {
          setIsRefreshingUpselling(false);
        }
      } catch (error) {
        console.error("Error fetching updated availability:", error);
        if (!isTimeSelection) {
          setIsRefreshingUpselling(false);
        }
      }
    },
    [
      currentBookingCart,
      selectedUpsellingService,
      selectedUpsellingTime,
      selectedQuantity,
      selectedDate,
      guestsCount,
      isVoucherBooking,
      rootData.servicesData,
      upsellingCategories,
    ]
  );

  useEffect(() => {
    if (upsellingCategories.length > 0 && selectedDate) {
      const formattedDate = selectedDate.toISOString().split("T")[0];
      const basketItemsCount = bookingCart?.BasketItems?.length || 0;
      const basketItemsIds =
        bookingCart?.BasketItems?.map((item: any) => item.BasketItemId) || [];
      const basketItemsKey = basketItemsIds.sort().join(",");
      const fetchKey = `${formattedDate}-${upsellingCategories.length}-${guestsCount.male}-${guestsCount.female}-${basketItemsCount}-${basketItemsKey}`;

      if (fetchKey === lastFetchKeyRef.current) {
        return;
      }

      lastFetchKeyRef.current = fetchKey;
      setLoading(true);
      onLoadingChange?.(true);

      const cartUrl = `/api/booking/cart`;
      fetch(cartUrl)
        .then((res) => res.json())
        .then((cart: any) => {
          const mainService = cart?.BasketItems?.find(
            (item: any) => item.Role === "MAIN"
          );
          const mainServiceStartTime = mainService?.StartTime || "";
          const mainServiceEndTime = mainService?.EndTime || "";

          const basketItems: any[] = [];
          if (selectedUpsellingService && selectedUpsellingTime.length > 0) {
            const cartItemsForService =
              cart?.BasketItems?.filter(
                (item: any) =>
                  item.Role === "INTEGRATION" &&
                  item.Service === selectedUpsellingService.Code
              ) || [];
            const existingTimeKeys = new Set(
              cartItemsForService.map(
                (item: any) =>
                  `${normalizeTime(item.StartTime)}-${normalizeTime(
                    item.EndTime
                  )}`
              )
            );

            selectedUpsellingTime.forEach((time: any) => {
              const timeKey = `${normalizeTime(time.StartTime)}-${normalizeTime(
                time.EndTime
              )}`;
              if (!existingTimeKeys.has(timeKey)) {
                const price = time.EffectivePrice?.[0] ||
                  time.Price || {
                  Price: 0,
                  Currency: "EUR",
                  VatIncluded: true,
                };
                const numPax = selectedUpsellingService?.NumPax || 1;
                const totalPersons = selectedQuantity * numPax;

                for (let i = 0; i < totalPersons; i++) {
                  const basketItem: any = {
                    Type: "SERVICE",
                    ServiceType: selectedUpsellingService.ProductType,
                    LineNr: basketItems.length + 1,
                    Service: selectedUpsellingService.Code,
                    Role: "INTEGRATION",
                    CheckinDate: selectedDate.toISOString().split("T")[0],
                    StartTime: time.StartTime,
                    EndTime: time.EndTime,
                    Quantity: {
                      Total: 1,
                      Man: 0,
                      Woman: 0,
                      ThirdType: 0,
                    },
                    GrossPrice: price,
                  };
                  basketItems.push(basketItem);
                }
              }
            });
          }

          const vouchers = isVoucherBooking
            ? currentBookingCart?.vouchers || []
            : [];
          const vouchersArray = isVoucherBooking
            ? vouchers.map((v: any) => ({
              VoucherCode: v.code,
              GiftCardType: v.giftCardType,
            }))
            : [];

          const formattedDate = selectedDate.toISOString().split("T")[0];
          const params: Record<string, string> = {
            checkinDate: formattedDate,
            startTime: mainServiceStartTime,
            endTime: mainServiceEndTime,
            basketItems: encodeURIComponent(JSON.stringify(basketItems)),
            men: guestsCount.male.toString(),
            women: guestsCount.female.toString(),
          };

          if (isVoucherBooking && vouchersArray.length > 0) {
            params.vouchers = encodeURIComponent(JSON.stringify(vouchersArray));
          }

          const url = `/api/booking/availability/upselling?${new URLSearchParams(
            params
          ).toString()}`;

          fetch(url)
            .then((res) => res.json())
            .then((response) => {
              const allServices: any[] = [];
              if (response && Array.isArray(response)) {
                response.forEach((service: any) => {
                  let serviceToSet = service;
                  const cmsService = getCmsServiceFromList(
                    rootData.servicesData,
                    serviceToSet.Code
                  );

                  if (cmsService) {
                    // Find the category for this service
                    // Category is an array, so we need to find the first matching upselling category
                    let matchedCategory = null;
                    const categories =
                      serviceToSet.Category || serviceToSet.category || [];
                    const categoryArray = Array.isArray(categories)
                      ? categories
                      : [categories];

                    if (categoryArray.length > 0) {
                      // Find the first category that matches an upselling category
                      // Category can be like "CAT_TREATMENTS.CAT_SINGLE_MASSAGE" and we need to match with "CAT_TREATMENTS"
                      for (const categoryItem of categoryArray) {
                        const categoryCode =
                          typeof categoryItem === "string"
                            ? categoryItem
                            : categoryItem?.code || categoryItem?.Code;

                        if (categoryCode) {
                          matchedCategory = upsellingCategories.find(
                            (cat: any) => {
                              const catCode = cat.acf?.code;
                              // Check if categoryCode includes the category code (e.g., "CAT_TREATMENTS.CAT_SINGLE_MASSAGE" includes "CAT_TREATMENTS")
                              return catCode && categoryCode.includes(catCode);
                            }
                          );

                          if (matchedCategory) {
                            break;
                          }
                        }
                      }
                    }

                    // If not found in API response, skip this service
                    if (!matchedCategory) {
                      return;
                    }

                    allServices.push({
                      ...serviceToSet,
                      cmsService,
                      category: matchedCategory,
                    });
                  }
                });
              }

              // Ghost services show_anyway (vedi commento nel primo fetch).
              const upsellingCatById2 = new Map<number, any>(
                (upsellingCategories || []).map((c: any) => [c.id, c]),
              );
              (rootData.servicesData || []).forEach((cmsService: any) => {
                const taxIds: number[] = Array.isArray(
                  cmsService?.service_category,
                )
                  ? cmsService.service_category
                  : [];
                const upsellingTaxId = taxIds.find((id) =>
                  upsellingCatById2.has(id),
                );
                if (upsellingTaxId === undefined) return;

                const codes = cmsService.acf?.codes || [];
                const showAnywayCodes = codes.filter((c: any) => {
                  const f = c?.show_anyway;
                  return f === true || f === 1 || f === "1" || f === "true";
                });
                // Nel flusso VOUCHER le card "show_anyway" (info-only) non vanno
          // mostrate: si selezionano solo i servizi coperti dal voucher.
          if (isVoucherBooking || showAnywayCodes.length === 0) return;

                const sibling = allServices.find(
                  (s) => s.cmsService?.id === cmsService.id,
                );
                const matchedCategory =
                  sibling?.category ||
                  upsellingCatById2.get(upsellingTaxId);
                if (!matchedCategory) return;

                showAnywayCodes.forEach((codeObj: any) => {
                  const code = codeObj.code;
                  if (!code) return;
                  if (allServices.some((s) => s.Code === code)) return;

                  allServices.push({
                    Code: code,
                    Type: "ShowAnyway",
                    Name:
                      codeObj.short_description ||
                      cmsService.acf?.title ||
                      code,
                    isShowAnyway: true,
                    showAnywayText: codeObj.show_anyway_text || "",
                    Availability: [],
                    cmsService,
                    category: matchedCategory,
                  });
                });
              });

              setUpsellingServices(allServices);
              setLoading(false);
              onLoadingChange?.(false);
            })
            .catch((error) => {
              console.error("Error fetching upselling services:", error);
              setLoading(false);
              onLoadingChange?.(false);
            });
        })
        .catch((error) => {
          console.error("Error fetching cart:", error);
          setLoading(false);
          onLoadingChange?.(false);
        });
    }
  }, [
    upsellingCategories,
    selectedDate,
    guestsCount,
    rootData.servicesData,
    bookingCart,
  ]);

  useEffect(() => {
    if (!selectedUpsellingService || selectedUpsellingTime.length === 0) {
      previousSelectedTimesRef.current = selectedUpsellingTime;
      return;
    }

    const currentTimesKey = selectedUpsellingTime
      .map(
        (t: any) => `${normalizeTime(t.StartTime)}-${normalizeTime(t.EndTime)}`
      )
      .sort()
      .join(",");
    const previousTimesKey = previousSelectedTimesRef.current
      .map(
        (t: any) => `${normalizeTime(t.StartTime)}-${normalizeTime(t.EndTime)}`
      )
      .sort()
      .join(",");

    if (currentTimesKey === previousTimesKey) {
      return;
    }

    if (isRefreshingRef.current) {
      previousSelectedTimesRef.current = selectedUpsellingTime;
      return;
    }

    isRefreshingRef.current = true;
    setIsLoadingTime(true);

    const refresh = async () => {
      try {
        await refreshAvailability(true);
        setIsLoadingTime(false);
        if (justDeselectedTimeRef.current) {
          justDeselectedTimeRef.current = null;
        }
      } catch (error) {
        console.error("Error refreshing availability:", error);
        setIsLoadingTime(false);
        if (justDeselectedTimeRef.current) {
          justDeselectedTimeRef.current = null;
        }
      } finally {
        isRefreshingRef.current = false;
        previousSelectedTimesRef.current = selectedUpsellingTime;
      }
    };

    refresh();
  }, [selectedUpsellingTime, selectedUpsellingService, refreshAvailability]);

  const lastRecoveredTimesRef = useRef<string>("");
  const userDeselectedAllRef = useRef<boolean>(false);
  const previousQuantityRef = useRef<number>(selectedQuantity);

  useEffect(() => {
    if (selectedUpsellingService) {
      const initialQuantity = calculateInitialQuantity(
        selectedUpsellingService
      );
      if (selectedQuantity !== initialQuantity) {
        setSelectedQuantity(initialQuantity);
      }
    }
  }, [selectedUpsellingService, totalGuests]);

  useEffect(() => {
    if (
      selectedUpsellingService &&
      selectedQuantity > previousQuantityRef.current &&
      selectedUpsellingTime.length > 0 &&
      onSetSelectedTimes
    ) {
      onSetSelectedTimes([]);
    }
    previousQuantityRef.current = selectedQuantity;
  }, [
    selectedQuantity,
    selectedUpsellingService,
    selectedUpsellingTime,
    onSetSelectedTimes,
  ]);

  useEffect(() => {
    if (
      selectedUpsellingService &&
      selectedUpsellingService.Code &&
      bookingCart &&
      !userDeselectedAllRef.current
    ) {
      const cartItemsForService =
        bookingCart.BasketItems?.filter(
          (item: any) =>
            item.Role === "INTEGRATION" &&
            item.Service === selectedUpsellingService.Code
        ) || [];

      if (cartItemsForService.length > 0) {
        const serviceKey = `${selectedUpsellingService.Code}-${bookingCart.BasketItems?.length || 0
          }`;

        if (lastRecoveredTimesRef.current === serviceKey) {
          return;
        }

        const hasSelectedTimes = selectedUpsellingTime.length > 0;
        const cartTimeKeys = new Set(
          cartItemsForService.map(
            (item: any) =>
              `${normalizeTime(item.StartTime)}-${normalizeTime(item.EndTime)}`
          )
        );
        const selectedTimeKeys = new Set(
          selectedUpsellingTime.map(
            (t: any) =>
              `${normalizeTime(t.StartTime)}-${normalizeTime(t.EndTime)}`
          )
        );

        const timesAreMatching = Array.from(cartTimeKeys as Set<string>).every(
          (key: string) => selectedTimeKeys.has(key)
        );

        if (!hasSelectedTimes || !timesAreMatching) {
          if (upsellingServices.length > 0) {
            const serviceWithAvailability = upsellingServices.find(
              (service: any) => service.Code === selectedUpsellingService.Code
            );

            if (
              serviceWithAvailability &&
              serviceWithAvailability.Availability
            ) {
              const selectedTimes: any[] = [];

              cartItemsForService.forEach((cartItem: any) => {
                const matchingAvailability =
                  serviceWithAvailability.Availability.find(
                    (avail: any) =>
                      timesMatch(avail.StartTime, cartItem.StartTime) &&
                      timesMatch(avail.EndTime, cartItem.EndTime)
                  );

                if (
                  matchingAvailability &&
                  !selectedTimes.some(
                    (t: any) =>
                      timesMatch(t.StartTime, matchingAvailability.StartTime) &&
                      timesMatch(t.EndTime, matchingAvailability.EndTime)
                  )
                ) {
                  selectedTimes.push(matchingAvailability);
                }
              });

              if (selectedTimes.length > 0) {
                lastRecoveredTimesRef.current = serviceKey;
                if (onSetSelectedTimes) {
                  onSetSelectedTimes(selectedTimes);
                } else {
                  selectedTimes.forEach((time: any) => {
                    const isAlreadySelected = selectedUpsellingTime.some(
                      (t: any) =>
                        timesMatch(t.StartTime, time.StartTime) &&
                        timesMatch(t.EndTime, time.EndTime)
                    );
                    if (!isAlreadySelected) {
                      onSelectTime(time);
                    }
                  });
                }
              }
            }
          } else {
            lastRecoveredTimesRef.current = serviceKey;
            setIsLoadingTime(true);
            setTimeout(async () => {
              await refreshAvailability();
              setIsLoadingTime(false);
            }, 100);
          }
        }
      } else if (selectedUpsellingTime.length > 0) {
        lastRecoveredTimesRef.current = "";
      }
    }
  }, [
    selectedUpsellingService,
    bookingCart,
    selectedUpsellingTime,
    upsellingServices,
    onSelectTime,
    onSetSelectedTimes,
    refreshAvailability,
  ]);

  useEffect(() => {
    if (
      !selectedUpsellingService &&
      previousSelectedServiceRef.current !== null &&
      upsellingCategories.length > 0 &&
      selectedDate
    ) {
      setIsLoadingTime(true);
      setTimeout(async () => {
        await refreshAvailability();
        setIsLoadingTime(false);
      }, 100);
    }
    previousSelectedServiceRef.current = selectedUpsellingService;
  }, [
    selectedUpsellingService,
    upsellingCategories,
    selectedDate,
    refreshAvailability,
  ]);

  return (
    <div
      id="step-upselling"
      className="w-full max-w-4xl mx-auto flex flex-col py-8 px-8"
    >
      <AnimatePresence mode="wait">
        {!selectedUpsellingService && currentCategoryGroup && (
          <motion.div
            onUpdate={keepAnimationOnMainThread}
            key={`upselling-header-${currentCategoryIndex}`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            style={{ overflow: "hidden" }}
          >
            <h3
              className="font-bold text-xl text-center px-8 lg:px-0"
              dangerouslySetInnerHTML={{
                __html:
                  currentCategoryGroup.category?.name ||
                  currentCategoryGroup.category?.acf?.title ||
                  "",
              }}
            />
            {currentCategoryGroup.category?.description && (
              <div
                className="text-center mt-2 px-8 lg:px-0 mb-6"
                dangerouslySetInnerHTML={{
                  __html: currentCategoryGroup.category.description,
                }}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>
      <div className="mt-0">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-8 space-y-2">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-solid border-secondary border-t-transparent"></div>
            <span className="text-sm text-gray-600">
              {t("Caricamento servizi...")}
            </span>
          </div>
        ) : upsellingServices.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <span className="text-slate-400">
              {t("Nessun servizio aggiuntivo disponibile")}
            </span>
          </div>
        ) : (
          <div className="relative">
            <AnimatePresence mode="wait">
              {!selectedUpsellingService ? (
                <motion.div
                  onUpdate={keepAnimationOnMainThread}
                  key={`service-selection-${currentCategoryIndex}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  className="relative"
                >
                  {isRefreshingUpselling && (
                    <div className="absolute inset-0 bg-white bg-opacity-75 z-50 flex flex-col items-center justify-center rounded-2xl">
                      <div className="h-8 w-8 animate-spin rounded-full border-4 border-solid border-secondary border-t-transparent"></div>
                    </div>
                  )}
                  {currentCategoryGroup && (
                    <ServiceTypeSelect
                      services={currentCategoryGroup.services}
                      selectedValue={selectedUpsellingService}
                      bookingCart={currentBookingCart}
                      isVoucherBooking={isVoucherBooking}
                      isUpselling={true}
                      loading={loading || isRefreshingUpselling}
                      totalGuests={totalGuests}
                      onSelect={(cmsService, apiService) => {
                        setSelectedQuantity(
                          calculateInitialQuantity(apiService)
                        );
                        onSelectService(cmsService, apiService);
                      }}
                    />
                  )}
                </motion.div>
              ) : (
                <motion.div
                  onUpdate={keepAnimationOnMainThread}
                  key="time-selection"
                  className="w-full"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                >
                  {(() => {
                    const serviceToUse =
                      currentSelectedService || selectedUpsellingService;
                    const cmsService = serviceToUse?.Code
                      ? getCmsServiceFromList(
                        rootData.servicesData,
                        serviceToUse.Code
                      )
                      : null;
                    const displayImage =
                      cmsService?.acf?.codes?.find(
                        (c: any) => c.code === serviceToUse?.Code
                      )?.image?.id || cmsService?.acf?.image?.id;
                    const displayTitle =
                      cmsService?.acf?.codes?.find(
                        (c: any) => c.code === serviceToUse?.Code
                      )?.short_description || serviceToUse?.Name;
                    const displayDescription = cmsService?.acf?.codes?.find(
                      (c: any) => c.code === serviceToUse?.Code
                    )?.long_description;

                    const numPax = serviceToUse?.NumPax || 1;
                    const maxQuantity =
                      numPax > 1
                        ? Math.floor(totalGuests / numPax)
                        : totalGuests;
                    const showQuantitySelector = maxQuantity > 1;

                    return (
                      <div
                        className={cn(
                          "mb-6 rounded-2xl overflow-hidden bg-white shadow-md mx-auto w-full max-w-lg"
                        )}
                      >
                        {displayImage && (
                          <div className="w-full h-60 relative">
                            <motion.img
                              onUpdate={keepAnimationOnMainThread}
                              alt=""
                              src={`/cms-asset/${displayImage}`}
                              className="w-full h-full object-cover object-center z-10"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{
                                duration: 0.3,
                                delay: 0.5,
                                ease: "easeInOut",
                              }}
                            />
                          </div>
                        )}
                        <div className="p-6">
                          <h4 className="text-secondary font-bold text-xl leading-none">
                            {displayTitle}
                          </h4>
                          {displayDescription && (
                            <div
                              dangerouslySetInnerHTML={{
                                __html: displayDescription,
                              }}
                              className="text-md text-gray-600 mt-2"
                            />
                          )}

                          {showQuantitySelector && (
                            <div className="flex flex-row items-center mt-4">
                              <div className="flex flex-col items-start">
                                <h4 className="font-semibold text-lg text-center">
                                  {t("Per quante persone?")}
                                </h4>
                                <p className="text-md text-gray-500">
                                  {t(
                                    "Indica per quante persone è destinato questo servizio."
                                  )}
                                </p>
                                <p className="text-md text-gray-500">
                                  {t("Es: 1 per 1 persona, 2 per 2 persone, ecc.")}
                                </p>
                              </div>
                              <div className="flex flex-row items-center bg-slate-100 rounded-sm py-3 px-4">
                                <Button
                                  onClick={() => {
                                    if (selectedQuantity > 1) {
                                      setSelectedQuantity(selectedQuantity - 1);
                                    }
                                  }}
                                  variant={"secondary"}
                                  className="rounded-full mr-2"
                                  size="icon"
                                  disabled={selectedQuantity <= 1}
                                >
                                  <Minus />
                                </Button>
                                <span className="w-8 flex flex-col items-center font-semibold">
                                  {selectedQuantity}
                                </span>
                                <Button
                                  onClick={() => {
                                    if (selectedQuantity < maxQuantity) {
                                      setSelectedQuantity(selectedQuantity + 1);
                                    }
                                  }}
                                  variant={"secondary"}
                                  className="rounded-full ml-2"
                                  size="icon"
                                  disabled={selectedQuantity >= maxQuantity}
                                >
                                  <Plus />
                                </Button>
                              </div>
                              {(() => {
                                const serviceToUse =
                                  currentSelectedService ||
                                  selectedUpsellingService;
                                return serviceToUse?.NumPax > 1 ? (
                                  <p className="text-sm text-gray-500 mt-2 text-center">
                                    {selectedQuantity * serviceToUse.NumPax}{" "}
                                    {selectedQuantity * serviceToUse.NumPax ===
                                      1
                                      ? t("persona")
                                      : t("persone")}{" "}
                                    {t("totali")}
                                  </p>
                                ) : null;
                              })()}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                  <h4 className="font-semibold text-lg mb-4 text-center">
                    {t("Seleziona uno o più orari")}
                  </h4>
                  {(() => {
                    const serviceToUse =
                      currentSelectedService || selectedUpsellingService;
                    const allTimes = serviceToUse?.Availability || [];

                    if (allTimes.length === 0) {
                      return (
                        <div className="flex items-center justify-center py-8">
                          <div className="text-center">
                            <p className="text-slate-600 mb-2">
                              {t(
                                "Ci scusiamo, non ci sono orari disponibili per questo servizio"
                              )}
                            </p>
                            <p className="text-sm text-slate-500">
                              {t("Prova a selezionare un altro servizio")}
                            </p>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div
                        className={cn(
                          "w-full grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 items-center gap-4"
                        )}
                      >
                        {allTimes.map((time: any, index: number) => {
                          const quantity = time.Quantity?.Total || 0;
                          const isSoldOut = quantity === 0;
                          const maxBookable = Math.min(
                            quantity,
                            time.MaxBookableQuantity || Infinity
                          );
                          const totalGuests =
                            guestsCount.male + guestsCount.female;

                          let isAvailable = false;
                          let maxAvailableQuantity = 0;
                          const serviceToUse =
                            currentSelectedService || selectedUpsellingService;
                          const numPax = serviceToUse?.NumPax || 1;
                          const totalPersonsNeeded = selectedQuantity * numPax;

                          if (!isSoldOut) {
                            if (serviceToUse?.GenderDistinction) {
                              const manAvailable = time.Quantity?.Man || 0;
                              const womanAvailable = time.Quantity?.Woman || 0;
                              const minGenderAvailable = Math.min(
                                manAvailable,
                                womanAvailable
                              );
                              maxAvailableQuantity = Math.min(
                                minGenderAvailable,
                                totalGuests
                              );
                              isAvailable =
                                manAvailable >= guestsCount.male &&
                                womanAvailable >= guestsCount.female &&
                                quantity > 0;
                            } else {
                              const maxCapacity = maxBookable * numPax;
                              maxAvailableQuantity = Math.min(
                                maxCapacity,
                                totalGuests
                              );
                              isAvailable =
                                maxAvailableQuantity >= totalPersonsNeeded &&
                                quantity > 0;
                            }
                          } else {
                            maxAvailableQuantity = 0;
                          }

                          const uniqueKey = `${normalizeTime(
                            time.StartTime
                          )}-${normalizeTime(time.EndTime)}-${index}`;
                          const timeId = `${normalizeTime(
                            time.StartTime
                          )}-${normalizeTime(time.EndTime)}`;
                          const isSelected = Array.isArray(
                            selectedUpsellingTime
                          )
                            ? selectedUpsellingTime.some(
                              (t: any) =>
                                timesMatch(t.StartTime, time.StartTime) &&
                                timesMatch(t.EndTime, time.EndTime)
                            )
                            : false;

                          const timeKey = `${normalizeTime(
                            time.StartTime
                          )}-${normalizeTime(time.EndTime)}`;
                          const wasJustDeselected =
                            justDeselectedTimeRef.current === timeKey;
                          const isTimeLoading =
                            isLoadingTime && (!isSelected || wasJustDeselected);

                          return (
                            <motion.div
                              onUpdate={keepAnimationOnMainThread}
                              key={uniqueKey}
                              className="relative"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{
                                duration: 0.3,
                                delay: index * 0.05,
                                ease: "easeInOut",
                              }}
                            >
                              <div
                                className={cn(
                                  "w-full rounded-2xl overflow-hidden bg-white shadow-md flex flex-row items-center justify-between px-4 py-2 relative",
                                  {
                                    "ring-2 ring-secondary":
                                      isSelected && !wasJustDeselected,
                                    "opacity-50 cursor-not-allowed":
                                      (isSoldOut && !isSelected) ||
                                      (!isAvailable &&
                                        !isSelected &&
                                        !isSoldOut) ||
                                      (isTimeLoading && !wasJustDeselected),
                                    "hover:bg-slate-50 cursor-pointer":
                                      !isSoldOut || isSelected,
                                    "animate-pulse":
                                      wasJustDeselected ||
                                      (isTimeLoading && !isSelected),
                                    "bg-gray-100": isSoldOut && !isSelected,
                                  }
                                )}
                                onClick={() => {
                                  if (
                                    (isAvailable || isSelected) &&
                                    !isLoadingTime &&
                                    !(isSoldOut && !isSelected)
                                  ) {
                                    // If already selected, handle deselection
                                    if (isSelected) {
                                      if (selectedUpsellingTime.length === 1) {
                                        userDeselectedAllRef.current = true;
                                      }
                                      handleTimeSelection(time);
                                      return;
                                    }
                                    // Handle new selection
                                    handleTimeSelection(time);
                                    const serviceToUse =
                                      currentSelectedService ||
                                      selectedUpsellingService;
                                    const numPaxForCheck =
                                      serviceToUse?.NumPax || 1;
                                    const totalPersonsForCheck =
                                      selectedQuantity * numPaxForCheck;
                                    if (totalPersonsForCheck > totalGuests) {
                                      const maxQuantity = Math.floor(
                                        totalGuests / numPaxForCheck
                                      );
                                      if (maxQuantity >= 1) {
                                        setSelectedQuantity(maxQuantity);
                                      }
                                    }
                                  }
                                }}
                              >
                                <div className="flex flex-col justify-center items-center w-full">
                                  <div className="flex flex-row items-center justify-center">
                                    <span>{time.StartTime.slice(0, 5)}</span>
                                  </div>
                                  {isSoldOut && !isSelected && (
                                    <span className="text-sm text-red-500 -mt-1 font-semibold">
                                      {t("Esaurito")}
                                    </span>
                                  )}
                                  {/* {!isAvailable &&
                                    !isSelected &&
                                    !isSoldOut && (
                                      <span className="text-sm text-gray-400 -mt-1">
                                        {t("Disponibilità")}:{" "}
                                        <span className="text-secondary font-semibold">
                                          0
                                        </span>
                                      </span>
                                    )} */}
                                  {/* {isAvailable && maxAvailableQuantity > 0 && (
                                    <span className="text-sm text-gray-400 -mt-1">
                                      {t("Disponibilità")}:{" "}
                                      <span className="text-secondary font-semibold">
                                        {Math.floor(
                                          maxAvailableQuantity / numPax
                                        )}
                                      </span>
                                    </span>
                                  )} */}
                                  {isSelected && quantity === 0 && (
                                    <span className="text-sm text-gray-400 -mt-1">
                                      <span className="text-secondary font-semibold">
                                        Disponibile
                                      </span>
                                    </span>
                                  )}
                                  {(() => {
                                    const price =
                                      time.EffectivePrice?.[0]?.Price ||
                                      time.Price;
                                    return price && price > 0 ? (
                                      <span className="text-sm text-secondary font-semibold">
                                        €
                                        {price.toLocaleString(i18n.language, {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        })}
                                      </span>
                                    ) : null;
                                  })()}
                                </div>
                                {(isTimeLoading || wasJustDeselected) && (
                                  <div className="absolute top-0 left-0 z-20 bg-white opacity-50 text-secondary rounded-2xl w-full h-full flex items-center justify-center">
                                    <Loader2 className="h-5 w-5 animate-spin" />
                                  </div>
                                )}
                              </div>
                              {isSelected && (
                                <div className="absolute top-2 right-2 z-20 bg-secondary text-white rounded-full w-5 h-5 flex items-center justify-center">
                                  <Check className="h-3 w-3" />
                                </div>
                              )}
                            </motion.div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>

      <div className="w-full flex flex-row items-center justify-center space-x-2 mt-4">
        {!selectedUpsellingService ? (
          <>
            {!loading && (
              <div className="w-full flex flex-row items-center justify-center space-x-2 mt-4">
                <Button
                  variant="muted"
                  onClick={() => {
                    if (currentCategoryIndex > 0) {
                      setCurrentCategoryIndex((i) => i - 1);
                    } else {
                      onCancel();
                    }
                  }}
                >
                  <span>{t("Indietro")}</span>
                </Button>
                <Button
                  variant={"secondary"}
                  onClick={() => {
                    if (isLastCategory) {
                      if (isMobile && onOpenCart) {
                        onOpenCart();
                      } else {
                        onSkip();
                      }
                    } else {
                      advanceCategory();
                    }
                  }}
                >
                  <span>
                    {isLastCategory
                      ? t("Concludi prenotazione")
                      : t("Salta")}
                  </span>
                </Button>
              </div>
            )}
          </>
        ) : (
          <>
            <Button
              variant={"muted"}
              onClick={() => {
                onBack();
              }}
            >
              <span>{t("Indietro")}</span>
            </Button>
            {(() => {
              const hasServiceInCart = bookingCart?.BasketItems?.some(
                (item: any) =>
                  item.Role === "INTEGRATION" &&
                  item.Service === selectedUpsellingService?.Code
              );

              const canContinue =
                selectedUpsellingTime.length > 0 ||
                (hasServiceInCart && selectedUpsellingTime.length === 0);

              // Loader nel pulsante anche durante la selezione/aggiornamento
              // dell'orario (isLoadingTime), non solo durante l'add-to-cart.
              const showButtonLoader = externalLoading || isLoadingTime;

              return (
                <Button
                  disabled={
                    loading ||
                    isLoadingTime ||
                    selectedQuantity <= 0 ||
                    !canContinue
                  }
                  variant={"secondary"}
                  onClick={() => {
                    // Aggiunge l'upselling al carrello; al termine il genitore
                    // azzera il servizio selezionato e si torna alla lista
                    // servizi della categoria corrente (nessun avanzamento auto).
                    onContinue(selectedQuantity);
                  }}
                  className={cn("relative", {
                    "animate-pulse": showButtonLoader,
                  })}
                >
                  <span
                    className={cn("transition-opacity duration-300", {
                      "opacity-0": showButtonLoader,
                    })}
                  >
                    {t("Aggiungi al carrello")}
                  </span>
                  {showButtonLoader && (
                    <div className="absolute w-full h-full flex items-center justify-center">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-solid border-white border-t-transparent"></div>
                    </div>
                  )}
                </Button>
              );
            })()}
          </>
        )}
      </div>
    </div>
  );
};

export default UpsellingStep;
